import {IAddress, IFormInput} from '../../../../i';
import {ValidationMessages} from '../../../../../util';

const CSV_REQUIRED_FIELDS = ['firstName', 'lastName', 'address1', 'city', 'state', 'postalCode', 'country'] as const;
const HIDDEN_OPTIONAL_CSV_FIELDS = ['companyName', 'address3', 'address4'] as const;
const COUNTRIES_REQUIRING_STATE = new Set(['us', 'ca']);
const STATE_REQUIRED_MIN_LENGTH = 2;
const STATE_OPTIONAL_MIN_LENGTH = 0;

export type CsvAddressRow = Record<string, string>;

export type ParseCsvAddressesSuccess = {
    ok: true;
    rows: CsvAddressRow[];
    warning?: string;
};

export type ParseCsvAddressesFailure = {
    ok: false;
    error: string;
};

export type ParseCsvAddressesResult = ParseCsvAddressesSuccess | ParseCsvAddressesFailure;

export type ImportCsvAddressesSuccess = {
    ok: true;
    addresses: IAddress[];
    warning?: string;
};

export type ImportCsvAddressesFailure = {
    ok: false;
    error: string;
};

export type ImportCsvAddressesResult = ImportCsvAddressesSuccess | ImportCsvAddressesFailure;

function tooFewRowsWarning(rowCount: number, qty: number): string {
    return `CSV contained ${rowCount} address${rowCount > 1 ? 'es' : ''}, but your cart has ${qty} items. The rest will be left empty.`;
}

function tooManyRowsWarning(rowCount: number, qty: number): string {
    return `CSV contained ${rowCount} addresses, but your cart only has ${qty} items. Extra rows were ignored.`;
}

export function countryRequiresState(country?: string): boolean {
    return COUNTRIES_REQUIRING_STATE.has((country || '').trim().toLowerCase());
}

function isRequiredValueField(field: (typeof CSV_REQUIRED_FIELDS)[number], country?: string): boolean {
    if (field === 'state') {
        return countryRequiresState(country);
    }

    return true;
}

function missingRequiredFieldNames(row: CsvAddressRow): string[] {
    return CSV_REQUIRED_FIELDS.filter(field => isRequiredValueField(field, row.country) && !(row[field] || '').trim());
}

function requiredValueFieldsForAddress(address: IAddress): Array<(typeof CSV_REQUIRED_FIELDS)[number]> {
    return CSV_REQUIRED_FIELDS.filter(field => isRequiredValueField(field, address.country?.value));
}

function isHiddenOptionalCsvField(key: string): boolean {
    return (HIDDEN_OPTIONAL_CSV_FIELDS as readonly string[]).includes(key);
}

function hiddenOptionalImportWarning(addressIndex: number, field: string, message: string): string {
    return `Shipping Address ${addressIndex + 1} ${field} is invalid (${message}). It is not shown in the form; omit that column or reimport a valid value to save.`;
}

function incompleteCsvRowsWarning(rows: CsvAddressRow[], csvRowCount: number): string | undefined {
    const messages: string[] = [];
    const limit = Math.min(csvRowCount, rows.length);

    for (let index = 0; index < limit; index++) {
        const missing = missingRequiredFieldNames(rows[index]);

        if (missing.length > 0) {
            messages.push(`Shipping Address ${index + 1} is missing ${missing.join(', ')}`);
        }
    }

    if (messages.length === 0) {
        return undefined;
    }

    return `${messages.join('. ')}. Fill in the required fields before saving, or those addresses will not be kept.`;
}

function combineWarnings(...warnings: Array<string | undefined>): string | undefined {
    const present = warnings.filter((warning): warning is string => Boolean(warning));

    return present.length > 0 ? present.join(' ') : undefined;
}

export function hasIncompleteRequiredCsvFields(address: IAddress): boolean {
    const required = requiredValueFieldsForAddress(address);
    const filledCount = required.filter(field => (address[field]?.value || '').trim()).length;

    return filledCount > 0 && filledCount < required.length;
}

export function markIncompleteRequiredFields(address: IAddress): void {
    if (!hasIncompleteRequiredCsvFields(address)) {
        return;
    }

    requiredValueFieldsForAddress(address).forEach(field => {
        const input = address[field];

        if (!input || (input.value || '').trim()) {
            return;
        }

        input.valid.alive = true;
        input.valid.isError = true;
        input.valid.msg = `Please enter your ${input.placeholder.toLowerCase()}`;
    });
}

export function clearEmptyOptionalFormInput(input?: IFormInput): void {
    if (!input || (input.value || '').trim()) {
        return;
    }

    if (input.valid.isError || input.valid.msg || input.valid.alive) {
        input.valid.isError = false;
        input.valid.msg = '';
        input.valid.alive = false;
    }
}

export function syncMultilineStateRequirement(address: IAddress): void {
    const state = address.state;

    if (!state) {
        return;
    }

    const requiresState = countryRequiresState(address.country?.value);
    const nextMinLength = requiresState ? STATE_REQUIRED_MIN_LENGTH : STATE_OPTIONAL_MIN_LENGTH;

    if (state.valid.minLength !== nextMinLength) {
        state.valid.minLength = nextMinLength;
    }

    if (!requiresState) {
        clearEmptyOptionalFormInput(state);
    }
}

export function syncMultilineFormFieldRules(address: IAddress): void {
    syncMultilineStateRequirement(address);
    clearEmptyOptionalFormInput(address.address2);
}

export function applyImportedFormInput(input: IFormInput, value: string): IFormInput {
    const nextValue = value || '';

    if (nextValue === '') {
        return {
            ...input,
            value: '',
            valid: {
                ...input.valid,
                msg: '',
                isError: false,
                alive: false,
            },
        };
    }

    let msg = '';

    if (input.valid.rgx && input.valid.rgx.length > 0) {
        const re = new RegExp(input.valid.rgx, 'gi');

        if (!re.test(nextValue)) {
            msg = input.valid.rgx_msg || '';
        }
    }

    if (msg.length < 1) {
        if (nextValue.length < input.valid.minLength) {
            msg = ValidationMessages.TooShort;
        } else if (nextValue.length > input.valid.maxLength) {
            msg = ValidationMessages.TooLong;
        }
    }

    return {
        ...input,
        value: nextValue,
        valid: {
            ...input.valid,
            msg,
            isError: msg.length > 0,
            alive: true,
        },
    };
}

export function parseCsvAddresses(text: string, qty: number): ParseCsvAddressesResult {
    try {
        const normalized = text.replace(/^\uFEFF/, '');

        if (!normalized.trim()) {
            return {ok: false, error: 'The CSV file is empty.'};
        }

        const lines = normalized.trim().split('\n');
        const headers = lines[0]
            .split(';')
            .map(i => i.trim())
            .filter(i => i);
        const missingHeaders = CSV_REQUIRED_FIELDS.filter(field => !headers.includes(field));

        if (missingHeaders.length > 0) {
            return {ok: false, error: `Missing required fields in CSV: ${missingHeaders.join(', ')}`};
        }

        const rows = lines.slice(1).map(line => {
            const values = line.split(';').map(v => v.trim());
            const rowObj: CsvAddressRow = {};

            headers.forEach((header, i) => {
                rowObj[header] = values[i] || '';
            });

            return rowObj;
        });

        const csvRowCount = rows.length;
        let resultRows = rows;
        let countWarning: string | undefined;

        if (rows.length < qty) {
            const blanksToAdd = qty - rows.length;
            const blankRow = () => Object.fromEntries(headers.map(header => [header, '']));

            resultRows = [...rows, ...Array.from({length: blanksToAdd}, blankRow)];
            countWarning = tooFewRowsWarning(rows.length, qty);
        } else if (rows.length > qty) {
            resultRows = rows.slice(0, qty);
            countWarning = tooManyRowsWarning(rows.length, qty);
        }

        const warning = combineWarnings(countWarning, incompleteCsvRowsWarning(resultRows, csvRowCount));

        return warning ? {ok: true, rows: resultRows, warning} : {ok: true, rows: resultRows};
    } catch {
        return {ok: false, error: 'Could not parse the CSV file.'};
    }
}

function applyCsvRowsToAddresses(existing: IAddress[], rows: CsvAddressRow[]): {addresses: IAddress[]; warning?: string} {
    const hiddenWarnings: string[] = [];

    const addresses = existing.map((existingAddress, index) => {
        const rowData = rows[index];
        const updated = {...existingAddress};

        if (!rowData) {
            return updated;
        }

        for (const key in rowData) {
            const value = rowData[key];

            if (key === 'isBusinessAddress') {
                updated[key] = value === 'true';
            } else if (updated[key]) {
                const next = applyImportedFormInput(updated[key], value);

                if (isHiddenOptionalCsvField(key) && next.valid.isError) {
                    hiddenWarnings.push(hiddenOptionalImportWarning(index, key, next.valid.msg));
                }

                updated[key] = next;
            }
        }

        HIDDEN_OPTIONAL_CSV_FIELDS.forEach(field => {
            if (!(field in rowData) && updated[field]) {
                updated[field] = applyImportedFormInput(updated[field], '');
            }
        });

        markIncompleteRequiredFields(updated);
        syncMultilineFormFieldRules(updated);

        return updated;
    });

    return {addresses, warning: hiddenWarnings.length > 0 ? hiddenWarnings.join(' ') : undefined};
}

export function importCsvAddresses(existing: IAddress[], text: string, qty: number): ImportCsvAddressesResult {
    const parsed = parseCsvAddresses(text, qty);

    if (!parsed.ok) {
        return parsed;
    }

    const applied = applyCsvRowsToAddresses(existing, parsed.rows);
    const warning = combineWarnings(parsed.warning, applied.warning);

    return warning ? {ok: true, addresses: applied.addresses, warning} : {ok: true, addresses: applied.addresses};
}
